import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-store.js';
import { getDiscordAuthMessage } from '../lib/auth-messages.js';

const DISCORD_OAUTH_START_URL =
  (import.meta.env.VITE_DISCORD_OAUTH_START_URL as string | undefined) ?? '/api/auth/discord';

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const discordError = getDiscordAuthMessage(searchParams.get('discord_error'));
  const login = useAuth((s) => s.login);
  const error = useAuth((s) => s.error);
  const status = useAuth((s) => s.status);
  const [pseudo, setPseudo] = useState('');
  const [password, setPassword] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (searchParams.has('discord_error')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('discord_error');
      setSearchParams(nextParams, { replace: true });
    }
    try {
      await login({ pseudo, password });
      void navigate('/');
    } catch {
      /* error displayed below */
    }
  }

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center gap-6">
      <div className="w-full max-w-sm flex flex-col items-center gap-6">
        <img src="/logo.png" alt="Foxhole War-Plan" className="w-80 h-80 object-contain" />
        <div className="w-full space-y-4 rounded-lg bg-slate-900 p-6 shadow-lg">
          <h1 className="text-2xl font-bold text-center">Login</h1>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <label className="block text-sm">
              Login
              <input
                className="mt-1 w-full rounded bg-slate-800 px-3 py-2"
                value={pseudo}
                onChange={(e) => setPseudo(e.target.value)}
                autoComplete="username"
                required
                minLength={2}
                maxLength={32}
              />
            </label>
            <label className="block text-sm">
              Password
              <input
                type="password"
                className="mt-1 w-full rounded bg-slate-800 px-3 py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            {(discordError ?? error) && (
              <p role="alert" className="text-sm text-red-400">{discordError ?? error}</p>
            )}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full rounded bg-emerald-600 py-2 font-medium hover:bg-emerald-500 disabled:opacity-60"
            >
              {status === 'loading' ? 'Signing in…' : 'Login'}
            </button>
          </form>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="h-px flex-1 bg-slate-700" />
            <span>or</span>
            <span className="h-px flex-1 bg-slate-700" />
          </div>
          <a
            href={DISCORD_OAUTH_START_URL}
            className="flex w-full items-center justify-center rounded bg-[#5865F2] py-2 font-medium text-white hover:bg-[#4752c4]"
          >
            Sign in with Discord
          </a>
        </div>
      </div>
    </main>
  );
}
